import React from "react";
import { SubmissionData } from "../FormStepper/FormStepper";

export type BulkUploadReducerAction =
  | { type: "clear" }
  | { type: "add"; files: FileArrayItem[] }
  | { type: "remove"; index: number }
  | { type: "markProcessed"; index: number; pfsId: string }
  | { type: "removeFilter"; filter: (item: FileArrayItem) => boolean };

export interface FileArrayItem {
  error?: boolean;
  message?: string;
  file?: File;
  name?: string;
  id?: string;
  processed?: boolean;
  pfsId?: string;
}

export interface ProcessedFile {
  fileName: string | undefined;
  fileId: string | undefined;
}

export type PfsApiMetadata = {
  id?: string;
  createdAt?: string;
  updatedAt?: string;
  parent?: string;
  context: {
    CUSTOMER_UPLOAD: {
      name: string;
    };
  };
  owner: {
    type: "XOMETRY";
    externalId?: string;
  };
  data?: {
    uploadUrl?: string;
    originalFileName?: string;
    md5?: string;
    mime?: string;
    size?: number;
  };
  sharedFileUsers: Array<{ type: "XOMETRY"; externalId: string }>;
};

export function constructPFSMetadata(
  userId: string,
  partName: string
): PfsApiMetadata {
  return {
    context: {
      CUSTOMER_UPLOAD: {
        name: partName,
      },
    },
    owner: {
      type: "XOMETRY",
      externalId: userId,
    },
    sharedFileUsers: [],
  };
}

export type BulkUploadProcessor = () => Promise<Array<ProcessedFile>>;
export type AddFilesFunction = (files: File[] | File) => void;
export type RemoveFileFunction = (index: number) => void;
export type ClearAllFilesFunction = () => void;
export type ShowFileSelectorFunction = () => void;
export type GetFileInputFunction = () => HTMLInputElement | null;
export type BulkUploadDropZoneHocFunction = (
  wrapped: React.ComponentType,
  onDropEvent?: () => void
) => React.ComponentType;

export type BulkUploadContextType = {
  data: {
    files: FileArrayItem[];
  };
  actions: {
    processFiles: BulkUploadProcessor;
    addFiles: AddFilesFunction;
    removeFile: RemoveFileFunction;
    clearFiles: ClearAllFilesFunction;
    showFileSelector: ShowFileSelectorFunction;
    getFileInput: GetFileInputFunction;
    withDropZone: BulkUploadDropZoneHocFunction;
  };
};

export const defaultBulkUploadContext: BulkUploadContextType = {
  data: {
    files: [] as FileArrayItem[],
  },
  actions: {
    processFiles: (() => Promise.resolve([])) as BulkUploadProcessor,
    addFiles: (() => undefined) as AddFilesFunction,
    removeFile: (() => undefined) as RemoveFileFunction,
    clearFiles: (() => undefined) as ClearAllFilesFunction,
    showFileSelector: (() => undefined) as ShowFileSelectorFunction,
    getFileInput: (() => null) as GetFileInputFunction,
    withDropZone: (wrapped: React.ComponentType) => wrapped,
  },
};

// we add these fields
type onBehalfOfUserInput = {
  emailAddress: string;
  userId: string;
};

export type BulkUploadData = SubmissionData & {
  customerEmail: string;
  creationTime: string;
  partFiles: {
    fileId: string;
    fileName: string;
  }[];
  onBehalfOfUser: onBehalfOfUserInput | null;
};
